import React, { useEffect, useMemo, useState } from 'react';

const stepsPreset = [
  { label: 'Analyzing location data', hint: 'Local labor + material indices' },
  { label: 'Calibrating cost curves', hint: 'Market volatility and seasonality' },
  { label: 'Composing first question', hint: 'To narrow your estimate fast' },
];

const funFacts = [
  'Pro tip: Finishes can swing costs by 20–35%.',
  'Basements often add value at a lower cost per sq ft.',
  'Energy-efficient choices reduce lifetime spend, not just bills.',
  'Early scope clarity saves time and redesign costs later.',
];

const randBetween = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

const QuestionLoader = ({ projectData }) => {
  const [progress, setProgress] = useState(4);
  const [activeStep, setActiveStep] = useState(0);
  const [pulse, setPulse] = useState(false);

  const locationText = useMemo(() => {
    if (!projectData) return '';
    const { city, state, constructionType } = projectData;
    return `${constructionType?.replace('-', ' ') || 'project'} in ${city || ''}${city && state ? ', ' : ''}${state || ''}`;
  }, [projectData]);

  useEffect(() => {
    const tick = setInterval(() => {
      setProgress((p) => {
        const next = p + randBetween(2, 5);
        return next >= 92 ? 92 : next; // keep some suspense
      });
      setPulse((v) => !v);
    }, 400);
    const stepTimer = setInterval(() => {
      setActiveStep((s) => (s + 1) % stepsPreset.length);
    }, 2000);
    return () => {
      clearInterval(tick);
      clearInterval(stepTimer);
    };
  }, []);

  const currentFact = funFacts[(activeStep + randBetween(0, funFacts.length - 1)) % funFacts.length];

  return (
    <div className="min-h-screen w-full flex items-center justify-center" style={{ background: 'radial-gradient(1200px 600px at 20% -10%, #e6f7f3 0%, rgba(255,255,255,0) 60%), radial-gradient(900px 500px at 110% 50%, #f7efe8 0%, rgba(255,255,255,0) 60%)' }}>
      <div className="w-full max-w-2xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-6">
          <div className={`inline-flex items-center justify-center h-12 w-12 rounded-xl bg-emerald-100 ${pulse ? 'scale-105' : ''} transition-transform duration-300`}>
            <span className="text-emerald-600 text-xl">⚙️</span>
          </div>
          <h1 className="mt-4 text-2xl font-bold text-gray-900">Preparing your cost journey</h1>
          {locationText && (
            <p className="mt-1 text-gray-600 text-sm">for your {locationText}</p>
          )}
        </div>

        {/* Steps */}
        <div className="bg-white/80 backdrop-blur rounded-2xl border border-gray-200 shadow-sm p-5 mb-5">
          <div className="flex flex-col gap-3">
            {stepsPreset.map((s, idx) => {
              const isActive = idx === activeStep;
              return (
                <div key={idx} className="flex items-start gap-3">
                  <div className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold ${isActive ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-600'}`}>{idx + 1}</div>
                  <div className="flex-1">
                    <div className={`text-sm font-medium ${isActive ? 'text-gray-900' : 'text-gray-600'}`}>{s.label}</div>
                    <div className="text-xs text-gray-500">{s.hint}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Progress bar */}
          <div className="mt-5">
            <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-600 transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
              <span>Setting context</span>
              <span>{progress}%</span>
            </div>
          </div>
        </div>

        {/* Tip */}
        <div className="text-center text-sm text-gray-600">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 text-gray-700">
            <span>💡</span>
            <span>{currentFact}</span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default QuestionLoader;



